import { useState } from 'react';
import { ApiError, Avatar, Button, ErrorNote, Sheet, Stars, api, brl, cx, haptic } from '@mobi/web';
import type { Ride } from '../useRide.js';

const TAGS = ['Dirigiu bem', 'Carro limpo', 'Simpático', 'Pontual', 'Rota eficiente'];

/**
 * Fecha a corrida: o que foi pago, como foi pago, e a avaliação.
 *
 * O valor em dinheiro aparece já descontado do saldo — é o número que o
 * passageiro precisa ter na mão na hora de pagar o motorista.
 */
export function RatePrompt({ ride, onDone }: { ride: Ride; onDone: () => void }) {
  const [stars, setStars] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (stars === 0) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/rides/${ride.id}/rate`, {
        body: { stars, tags: tags.length > 0 ? tags : undefined, comment: comment || undefined },
      });
      onDone();
    } catch (err) {
      // Já avaliada é o mesmo desfecho do ponto de vista de quem usa:
      // a tela sai do caminho.
      if (err instanceof ApiError && err.status === 409) onDone();
      else setError(err instanceof ApiError ? err.message : 'não foi possível avaliar');
    } finally {
      setBusy(false);
    }
  }

  const cash = ride.cashDueCents ?? ride.finalFareCents ?? 0;
  const driver = ride.driver;

  return (
    /*
     * Painel de altura fixa, conteúdo alinhado ao topo. Ancorado embaixo,
     * qualquer coisa que aparecesse no meio empurrava as estrelas para
     * cima no instante do toque — e o clique fantasma do celular caía no
     * elemento errado.
     */
    <div className="flex h-full flex-col bg-surface-2">
      <Sheet className="h-full overflow-y-auto">
        <p className="text-sm font-medium text-muted">Viagem concluída</p>

        {driver && (
          <div className="mt-4 flex items-center gap-4">
            <Avatar
              name={driver.name}
              src={driver.hasPhoto ? `/api/rides/${ride.id}/photo/driver` : null}
              size="lg"
            />
            <div className="min-w-0">
              <p className="truncate text-xl font-bold">{driver.name}</p>
              <Stars value={driver.rating} count={driver.ratingCount} />
              {driver.vehicle && (
                <p className="mt-1 truncate text-sm text-muted">
                  {driver.vehicle.color} {driver.vehicle.model}
                  <span className="tabular ml-2 rounded-md bg-surface-2 px-1.5 py-0.5 text-xs font-semibold tracking-wide">
                    {driver.vehicle.plate}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 rounded-2xl bg-surface-2 px-4 py-3">
          <p className="display text-3xl">{brl(cash)}</p>
          <p className="mt-1 text-sm text-muted">
            {ride.walletAppliedCents > 0 ? (
              <>
                a pagar em dinheiro — {brl(ride.walletAppliedCents)} foram abatidos da sua
                carteira
              </>
            ) : (
              <>a pagar direto ao motorista</>
            )}
          </p>
        </div>

        <div className="mt-8">
          <p className="font-semibold">
            Como foi sua viagem{driver ? ` com ${driver.name.split(' ')[0]}` : ''}?
          </p>

          <div className="mt-3 flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                aria-label={`${n} estrela${n > 1 ? 's' : ''}`}
                onClick={() => {
                  setStars(n);
                  haptic();
                }}
                className="touch-manipulation p-1"
              >
                <svg
                  viewBox="0 0 20 20"
                  className={cx('h-9 w-9 transition', n <= stars ? 'fill-warn' : 'fill-line')}
                >
                  <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9 4.8 17.6l1-5.8L1.5 7.7l5.9-.9z" />
                </svg>
              </button>
            ))}
          </div>

          {/* Sempre à vista: escondidas até a estrela ser tocada, ninguém
              sabia que dava para escolher — e o toque na estrela parecia
              ter encerrado a avaliação. */}
          <div className="mt-4 flex flex-wrap gap-2">
              {TAGS.map((tag) => {
                const on = tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() =>
                      setTags(on ? tags.filter((t) => t !== tag) : [...tags, tag].slice(0, 5))
                    }
                    className={cx(
                      'rounded-full border px-3 py-2 text-sm transition',
                      on ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted',
                    )}
                  >
                    {tag}
                  </button>
                );
              })}
          </div>

          {stars > 0 && stars <= 3 && (
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="O que poderia ter sido melhor? (opcional)"
              rows={3}
              maxLength={500}
              className="mt-4 w-full rounded-2xl border border-line bg-surface-2 px-4 py-3 text-ink placeholder:text-faint outline-none focus:border-brand"
            />
          )}
        </div>

        {error && (
          <div className="mt-4">
            <ErrorNote>{error}</ErrorNote>
          </div>
        )}

        <div className="mt-6 space-y-2">
          <Button size="lg" full loading={busy} disabled={stars === 0} onClick={submit}>
            Enviar avaliação
          </Button>
          <Button variant="ghost" full onClick={onDone}>
            Agora não
          </Button>
        </div>
      </Sheet>
    </div>
  );
}
